import {
  classifyTvSupport,
  summarizeFrameTimes,
  summarizeRenderWork,
  type FrameMetrics,
  type RenderWorkMetrics,
} from "./benchmarkMetrics.js";
import {
  isWebGlStressLevelSafeForSoak,
  parseTvBenchmarkSubmission,
  webGlLongevityProfile,
  type TvBenchmarkProgressReport,
  type TvBenchmarkReport,
} from "../shared/tvBenchmark.js";

const BENCHMARK_VERSION = 8;
const WIDTH = 1280;
const HEIGHT = 720;
const DISPLAY_DURATION_MS = 2_500;
const RENDER_DURATION_MS = 3_000;
// Long enough to cover roughly three full chain-reaction burst cycles (see FUSE_BURST_CYCLE_MS)
// so p95/max render-work reflect repeated peak moments, not just one burst plus steady state.
const REPRESENTATIVE_SCENE_DURATION_MS = 8_000;
const SOAK_DURATION_MS = 10 * 60_000;
const SOAK_WINDOW_MS = 60_000;
const SOAK_PROGRESS_INTERVAL_MS = 250;
const WEBGL_CHECKPOINT_INTERVAL_MS = 30_000;
const WEBGL_PROGRESS_STORAGE_KEY = "tvGraphicsBenchmark:webglProgress";
const CANVAS_SOAK_TARGET_FPS = 30;
const WEBGL_STRESS_DURATION_MS = 1_500;

type WebGlDetails = Omit<TvBenchmarkReport["webgl2"], "metrics" | "representativeMetrics"> & {
  metrics: FrameMetrics | null;
  representativeMetrics: FrameMetrics | null;
};
type CpuMetrics = TvBenchmarkReport["javascript"];
type BenchmarkResult = TvBenchmarkReport;
type SceneMetrics = {
  frames: FrameMetrics;
  renderWork: RenderWorkMetrics;
};
type SoakMetrics = Omit<NonNullable<TvBenchmarkReport["soak"]>, "aggregate" | "windows"> & {
  aggregate: SceneMetrics;
  windows: Array<{
    minute: number;
    elapsedMs: number;
    metrics: SceneMetrics;
  }>;
};
type SoakCheckpoint = {
  elapsedMs: number;
  aggregate: SceneMetrics;
  latestWindow: SceneMetrics;
  lastFrameGapMs: number;
};

const canvas = requiredElement<HTMLCanvasElement>("benchmark-canvas");
const context2d = canvas.getContext("2d", { alpha: false });
const runButton = requiredElement<HTMLButtonElement>("run-benchmark");
const soakButton = requiredElement<HTMLButtonElement>("run-soak");
const webglSoakButton = requiredElement<HTMLButtonElement>("run-webgl-soak");
const progress = requiredElement<HTMLProgressElement>("benchmark-progress");
const benchmarkStatus = requiredElement<HTMLElement>("benchmark-status");
const benchmarkStep = requiredElement<HTMLElement>("benchmark-step");
const report = requiredElement<HTMLElement>("benchmark-report");
const jsonOutput = requiredElement<HTMLTextAreaElement>("benchmark-json");
const summary = requiredElement<HTMLElement>("benchmark-summary");
const copyButton = requiredElement<HTMLButtonElement>("copy-results");
const sendButton = requiredElement<HTMLButtonElement>("send-results");
const sendStatus = requiredElement<HTMLElement>("send-status");
const recovery = requiredElement<HTMLElement>("webgl-recovery");
const recoveryDetail = requiredElement<HTMLElement>("webgl-recovery-detail");
const sendRecoveredButton = requiredElement<HTMLButtonElement>("send-recovered-webgl");
const recoveredSendStatus = requiredElement<HTMLElement>("recovered-send-status");
const tvModelInput = requiredElement<HTMLInputElement>("tv-model");
let latestResult: BenchmarkResult | null = null;
let recoveredWebGlProgress: TvBenchmarkProgressReport | null = null;

runButton.addEventListener("click", function () {
  void runBenchmark();
});
soakButton.addEventListener("click", function () {
  void runFuseSoak();
});
webglSoakButton.addEventListener("click", function () {
  void runWebGlSoak();
});
copyButton.addEventListener("click", copyResults);
sendButton.addEventListener("click", function () {
  void sendResults();
});
sendRecoveredButton.addEventListener("click", function () {
  if (recoveredWebGlProgress) void sendRecoveredWebGlProgress(recoveredWebGlProgress);
});
drawIdleScene();
restoreInterruptedWebGlProgress();

async function runBenchmark(): Promise<void> {
  if (!context2d || runButton.disabled) return;
  runButton.disabled = true;
  soakButton.disabled = true;
  webglSoakButton.disabled = true;
  runButton.textContent = "Benchmark running…";
  progress.max = 7;
  progress.value = 0;
  report.classList.add("hidden");
  summary.className = "summary";
  summary.innerHTML = "<h2>Testing this TV…</h2><p>Keep this page visible until it finishes.</p>";

  try {
    setStage(1, "Measuring display cadence", "1 of 7");
    const display = await measureFrames(DISPLAY_DURATION_MS, drawDisplayFrame);
    setMetric("display", display.fps + " fps", formatFrameDetails(display));

    setStage(2, "Warming up Canvas 2D", "2 of 7");
    await measureFrames(500, drawCanvasWorkload);
    setStage(2, "Measuring 1280×720 Canvas 2D", "2 of 7");
    const canvasMetrics = await measureFrames(RENDER_DURATION_MS, drawCanvasWorkload);
    setMetric("canvas", canvasMetrics.fps + " fps", formatFrameDetails(canvasMetrics));

    setStage(3, "Measuring Fuse Runners compatibility scene", "3 of 7");
    await measureRepresentativeScene(500, drawFuseCompatibilityWorkload);
    const fuseCompatibility = await measureRepresentativeScene(
      REPRESENTATIVE_SCENE_DURATION_MS,
      drawFuseCompatibilityWorkload,
    );
    setMetric(
      "fuse-compat",
      fuseCompatibility.frames.fps + " fps",
      formatSceneDetails(fuseCompatibility),
    );

    setStage(4, "Measuring Fuse Runners rich scene", "4 of 7");
    await measureRepresentativeScene(500, drawFuseRichWorkload);
    const fuseRich = await measureRepresentativeScene(
      REPRESENTATIVE_SCENE_DURATION_MS,
      drawFuseRichWorkload,
    );
    setMetric("fuse-rich", fuseRich.frames.fps + " fps", formatSceneDetails(fuseRich));

    setStage(5, "Measuring JavaScript simulation", "5 of 7");
    const cpu = await measureCpuSimulation();
    setMetric(
      "cpu",
      formatCompactNumber(cpu.updatesPerSecond) + "/sec",
      cpu.updates.toLocaleString() + " deterministic vehicle updates",
    );

    setStage(6, "Checking WebGL2", "6 of 7");
    const webgl2 = await measureWebGl2();
    if (webgl2.available && webgl2.metrics) {
      const representative = webgl2.representativeMetrics;
      const lastStress = webgl2.stressLevels?.[webgl2.stressLevels.length - 1];
      setMetric(
        "webgl",
        (representative || webgl2.metrics).fps + " fps",
        "battle-like " +
          (representative ? formatFrameDetails(representative) : "not measured") +
          " · raw " +
          webgl2.metrics.fps +
          " fps · " +
          (lastStress ? lastStress.label + " " + lastStress.metrics.fps + " fps · " : "") +
          webgl2.renderer,
      );
    } else {
      setMetric(
        "webgl",
        "Unavailable",
        webgl2.error || "This browser did not provide a WebGL2 rendering context.",
      );
    }

    setStage(7, "Classifying this TV", "7 of 7");
    const classification = classifyTvSupport({
      canvas: canvasMetrics,
      webgl2: webgl2.metrics,
      webgl2Representative: webgl2.representativeMetrics,
      fuseRunners: {
        compatibility: fuseCompatibility.frames,
        rich: fuseRich.frames,
        compatibilityRenderWork: fuseCompatibility.renderWork,
        richRenderWork: fuseRich.renderWork,
      },
    });
    const result: BenchmarkResult = {
      benchmarkVersion: BENCHMARK_VERSION,
      capturedAt: new Date().toISOString(),
      environment: readEnvironment(),
      features: readFeatures(webgl2.available),
      display,
      canvas2d720p: canvasMetrics,
      representativeScenes: {
        fuseRunnersCompatibility: fuseCompatibility,
        fuseRunnersRich: fuseRich,
      },
      javascript: cpu,
      webgl2,
      classification,
    };
    showResult(result);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    benchmarkStatus.textContent = "Benchmark stopped";
    benchmarkStep.textContent = message;
    summary.className = "summary fail";
    summary.innerHTML = "<h2>Benchmark could not finish</h2><p>" + escapeHtml(message) + "</p>";
  } finally {
    runButton.disabled = false;
    runButton.textContent = "Run Benchmark Again";
    soakButton.disabled = latestResult === null;
    webglSoakButton.disabled =
      !latestResult?.webgl2.available || !latestResult.webgl2.representativeMetrics;
  }
}

async function runFuseSoak(): Promise<void> {
  if (!context2d || !latestResult || runButton.disabled || soakButton.disabled) return;
  runButton.disabled = true;
  soakButton.disabled = true;
  webglSoakButton.disabled = true;
  soakButton.textContent = "Canvas 30 fps soak running…";
  sendButton.disabled = true;
  report.classList.add("hidden");
  summary.className = "summary";
  summary.innerHTML =
    "<h2>Running the ten-minute Fuse soak…</h2>" +
    "<p>Keep this page visible. Results are recorded in one-minute windows.</p>";
  progress.max = SOAK_DURATION_MS;
  progress.value = 0;
  benchmarkStatus.textContent = "Fuse compatibility soak";
  benchmarkStep.textContent = "0 of 10 minutes";

  try {
    await measureRepresentativeScene(500, drawFuseCompatibilityWorkload);
    const soak = await measureSoak({
      kind: "fuse-runners-compatibility",
      draw: drawFuseCompatibilityWorkload,
      targetFps: CANVAS_SOAK_TARGET_FPS,
    });
    latestResult.soak = soak;
    latestResult.capturedAt = new Date().toISOString();
    setMetric(
      "fuse-compat",
      soak.aggregate.frames.fps + " fps soak",
      formatSceneDetails(soak.aggregate) + " · 10 one-minute windows",
    );
    showResult(latestResult);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    benchmarkStatus.textContent = "Soak stopped";
    benchmarkStep.textContent = message;
    summary.className = "summary fail";
    summary.innerHTML = "<h2>Soak could not finish</h2><p>" + escapeHtml(message) + "</p>";
  } finally {
    runButton.disabled = false;
    soakButton.disabled = latestResult === null;
    soakButton.textContent = latestResult?.soak
      ? "Run 10-Minute Canvas 30 FPS Soak Again"
      : "Run 10-Minute Canvas 30 FPS Soak";
    webglSoakButton.disabled =
      !latestResult?.webgl2.available || !latestResult.webgl2.representativeMetrics;
  }
}

async function runWebGlSoak(): Promise<void> {
  if (!latestResult?.webgl2.available || runButton.disabled || webglSoakButton.disabled) return;
  if (!latestResult.webgl2.representativeMetrics) {
    benchmarkStatus.textContent = "Run the quick benchmark before starting the WebGL soak.";
    return;
  }
  const selected = webGlLongevityProfile();
  const runtime = createWebGlStressRuntime();
  if (!runtime) {
    benchmarkStatus.textContent = "WebGL soak unavailable";
    benchmarkStep.textContent = "Could not create a fresh WebGL2 context.";
    return;
  }
  recovery.classList.add("hidden");
  const runId = createWebGlRunId();
  let sequence = 0;
  let lastCheckpoint: SoakCheckpoint | undefined;
  let contextLost = false;
  let submissionQueue = Promise.resolve();
  const recordProgress = (
    status: TvBenchmarkProgressReport["status"],
    checkpoint?: SoakCheckpoint,
    error?: string,
  ): Promise<void> => {
    const payload = createWebGlProgressReport({
      runId,
      sequence,
      status,
      selected,
      checkpoint,
      contextLost,
      error,
    });
    sequence += 1;
    persistWebGlProgress(payload);
    submissionQueue = submissionQueue.then(() => postProgressCheckpoint(payload));
    return submissionQueue;
  };
  const runtimeCanvas = runtime.gl.canvas as HTMLCanvasElement;
  const handleContextLost = (): void => {
    contextLost = true;
    void recordProgress(
      "context-lost",
      lastCheckpoint,
      "The WebGL2 context-lost event fired before the next frame completed.",
    );
  };
  const handlePageHide = (): void => {
    const payload = createWebGlProgressReport({
      runId,
      sequence,
      status: "page-hidden",
      selected,
      checkpoint: lastCheckpoint,
      contextLost,
      error: "The benchmark page was hidden or unloaded before the soak completed.",
    });
    persistWebGlProgress(payload);
  };
  runtimeCanvas.addEventListener("webglcontextlost", handleContextLost);
  window.addEventListener("pagehide", handlePageHide);

  runButton.disabled = true;
  soakButton.disabled = true;
  webglSoakButton.disabled = true;
  webglSoakButton.textContent = "WebGL 30 fps soak running…";
  sendButton.disabled = true;
  report.classList.add("hidden");
  summary.className = "summary";
  summary.innerHTML =
    "<h2>Running the ten-minute WebGL2 30 fps soak…</h2><p>" +
    escapeHtml(selected.label) +
    " · keep this page visible.</p>";
  progress.max = SOAK_DURATION_MS;
  progress.value = 0;
  benchmarkStatus.textContent = "WebGL2 30 fps soak";
  benchmarkStep.textContent = "0 of 10 minutes · crash checkpoint 0";

  try {
    void recordProgress("started");
    const draw = (time: number): void => {
      if (runtime.gl.isContextLost()) {
        contextLost = true;
        throw new Error("WebGL2 context was lost during the soak; the run stopped safely.");
      }
      drawRepresentativeWebGlStressFrame(
        runtime.gl,
        runtime.timeLocation,
        time,
        selected.instances,
        selected.passes,
      );
    };
    await measureRepresentativeScene(500, draw);
    const soak = await measureSoak({
      kind: "webgl2-representative",
      draw,
      targetFps: selected.targetFps,
      stressLabel: selected.label,
      checkpointIntervalMs: WEBGL_CHECKPOINT_INTERVAL_MS,
      onCheckpoint: function (checkpoint): void {
        lastCheckpoint = checkpoint;
        benchmarkStep.textContent =
          Math.min(10, Math.floor(checkpoint.elapsedMs / 60_000)) +
          " of 10 minutes · crash checkpoint " +
          sequence;
        void recordProgress("running", checkpoint);
      },
    });
    await recordProgress("completed", lastCheckpoint);
    latestResult.webgl2.soak = soak;
    latestResult.capturedAt = new Date().toISOString();
    setMetric(
      "webgl",
      soak.aggregate.frames.fps + " fps soak",
      selected.label + " · " + formatSceneDetails(soak.aggregate),
    );
    showResult(latestResult);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    await recordProgress(contextLost ? "context-lost" : "failed", lastCheckpoint, message);
    benchmarkStatus.textContent = "WebGL soak stopped";
    benchmarkStep.textContent = message;
    summary.className = "summary fail";
    summary.innerHTML = "<h2>WebGL soak could not finish</h2><p>" + escapeHtml(message) + "</p>";
  } finally {
    runtimeCanvas.removeEventListener("webglcontextlost", handleContextLost);
    window.removeEventListener("pagehide", handlePageHide);
    runtime.dispose();
    runButton.disabled = false;
    soakButton.disabled = latestResult === null;
    webglSoakButton.disabled =
      !latestResult?.webgl2.available || !latestResult.webgl2.representativeMetrics;
    webglSoakButton.textContent = latestResult?.webgl2.soak
      ? "Run WebGL Soak + Record Diagnostics Again"
      : "Run WebGL Soak + Record Crash Diagnostics";
  }
}

function measureFrames(
  durationMs: number,
  draw: (timeMs: number, frame: number) => void,
): Promise<FrameMetrics> {
  return new Promise(function (resolve) {
    const frameTimes: number[] = [];
    let firstTime = 0;
    let previousTime = 0;
    let frame = 0;
    function tick(time: number): void {
      if (firstTime === 0) {
        firstTime = time;
        previousTime = time;
      } else {
        frameTimes.push(time - previousTime);
        previousTime = time;
      }
      draw(time, frame);
      frame += 1;
      if (time - firstTime < durationMs) {
        window.requestAnimationFrame(tick);
      } else {
        resolve(summarizeFrameTimes(frameTimes));
      }
    }
    window.requestAnimationFrame(tick);
  });
}

function measureRepresentativeScene(
  durationMs: number,
  draw: (timeMs: number, frame: number) => void,
): Promise<SceneMetrics> {
  return new Promise(function (resolve) {
    const frameTimes: number[] = [];
    const renderTimes: number[] = [];
    let firstTime = 0;
    let previousTime = 0;
    let frame = 0;
    function tick(time: number): void {
      if (firstTime === 0) {
        firstTime = time;
        previousTime = time;
      } else {
        frameTimes.push(time - previousTime);
        previousTime = time;
      }
      const renderStartedAt = performance.now();
      draw(time, frame);
      renderTimes.push(performance.now() - renderStartedAt);
      frame += 1;
      if (time - firstTime < durationMs) {
        window.requestAnimationFrame(tick);
      } else {
        resolve({
          frames: summarizeFrameTimes(frameTimes),
          renderWork: summarizeRenderWork(renderTimes),
        });
      }
    }
    window.requestAnimationFrame(tick);
  });
}

function measureSoak(options: {
  kind: SoakMetrics["kind"];
  draw: (timeMs: number, frame: number) => void;
  targetFps: number;
  stressLabel?: string;
  checkpointIntervalMs?: number;
  onCheckpoint?: (checkpoint: SoakCheckpoint) => void;
}): Promise<SoakMetrics> {
  return new Promise(function (resolve, reject) {
    const allFrameTimes: number[] = [];
    const allRenderTimes: number[] = [];
    let windowFrameTimes: number[] = [];
    let windowRenderTimes: number[] = [];
    let checkpointFrameTimes: number[] = [];
    let checkpointRenderTimes: number[] = [];
    const windows: SoakMetrics["windows"] = [];
    let firstTime = 0;
    let previousDrawTime = 0;
    let lastFrameGapMs = 0;
    let nextDrawAt = 0;
    let lastProgressAt = 0;
    let nextWindowAt = SOAK_WINDOW_MS;
    let nextCheckpointAt = options.checkpointIntervalMs ?? Number.POSITIVE_INFINITY;
    let frame = 0;
    let hiddenStartedAt = 0;
    let hiddenDurationMs = 0;

    function handleVisibilityChange(): void {
      const now = performance.now();
      if (document.hidden) {
        hiddenStartedAt = now;
        return;
      }
      if (hiddenStartedAt > 0) {
        hiddenDurationMs += now - hiddenStartedAt;
        hiddenStartedAt = 0;
        previousDrawTime = 0;
        nextDrawAt = now;
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    function tick(time: number): void {
      if (firstTime === 0) firstTime = time;

      const currentHiddenDuration =
        hiddenStartedAt > 0 ? hiddenDurationMs + (time - hiddenStartedAt) : hiddenDurationMs;
      const elapsedMs = time - firstTime - currentHiddenDuration;
      const frameIntervalMs = options.targetFps > 0 ? 1000 / options.targetFps : 0;
      const shouldDraw = frameIntervalMs === 0 || nextDrawAt === 0 || time >= nextDrawAt;
      if (shouldDraw) {
        if (previousDrawTime > 0) {
          const frameTime = time - previousDrawTime;
          lastFrameGapMs = frameTime;
          allFrameTimes.push(frameTime);
          windowFrameTimes.push(frameTime);
          checkpointFrameTimes.push(frameTime);
        }
        previousDrawTime = time;
        const renderStartedAt = performance.now();
        try {
          options.draw(time, frame);
        } catch (error) {
          document.removeEventListener("visibilitychange", handleVisibilityChange);
          reject(error instanceof Error ? error : new Error(String(error)));
          return;
        }
        const renderTime = performance.now() - renderStartedAt;
        allRenderTimes.push(renderTime);
        windowRenderTimes.push(renderTime);
        checkpointRenderTimes.push(renderTime);
        frame += 1;
        if (frameIntervalMs > 0) {
          if (nextDrawAt === 0) nextDrawAt = time + frameIntervalMs;
          else {
            do {
              nextDrawAt += frameIntervalMs;
            } while (nextDrawAt <= time);
          }
        }
      }
      if (lastProgressAt === 0 || time - lastProgressAt >= SOAK_PROGRESS_INTERVAL_MS) {
        progress.value = Math.min(SOAK_DURATION_MS, elapsedMs);
        lastProgressAt = time;
      }
      if (elapsedMs >= nextWindowAt) {
        windows.push({
          minute: windows.length + 1,
          elapsedMs: round(elapsedMs),
          metrics: {
            frames: summarizeFrameTimes(windowFrameTimes),
            renderWork: summarizeRenderWork(windowRenderTimes),
          },
        });
        windowFrameTimes = [];
        windowRenderTimes = [];
        nextWindowAt += SOAK_WINDOW_MS;
        benchmarkStep.textContent =
          Math.min(10, windows.length) + " of 10 minutes · keep this page visible";
      }
      if (elapsedMs >= nextCheckpointAt) {
        options.onCheckpoint?.({
          elapsedMs: round(elapsedMs),
          aggregate: {
            frames: summarizeFrameTimes(allFrameTimes),
            renderWork: summarizeRenderWork(allRenderTimes),
          },
          latestWindow: {
            frames: summarizeFrameTimes(checkpointFrameTimes),
            renderWork: summarizeRenderWork(checkpointRenderTimes),
          },
          lastFrameGapMs: round(lastFrameGapMs),
        });
        checkpointFrameTimes = [];
        checkpointRenderTimes = [];
        do {
          nextCheckpointAt += options.checkpointIntervalMs ?? SOAK_DURATION_MS;
        } while (nextCheckpointAt <= elapsedMs);
      }

      if (elapsedMs < SOAK_DURATION_MS) {
        window.requestAnimationFrame(tick);
        return;
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      resolve({
        kind: options.kind,
        requestedDurationMs: SOAK_DURATION_MS,
        completedDurationMs: round(elapsedMs),
        ...(options.targetFps > 0 ? { targetFps: options.targetFps } : {}),
        ...(options.stressLabel ? { stressLabel: options.stressLabel } : {}),
        aggregate: {
          frames: summarizeFrameTimes(allFrameTimes),
          renderWork: summarizeRenderWork(allRenderTimes),
        },
        windows,
      });
    }

    window.requestAnimationFrame(tick);
  });
}

function drawDisplayFrame(time: number): void {
  if (!context2d) return;
  context2d.fillStyle = "#07111f";
  context2d.fillRect(0, 0, WIDTH, HEIGHT);
  context2d.strokeStyle = "#233a54";
  context2d.lineWidth = 18;
  context2d.beginPath();
  context2d.arc(WIDTH / 2, HEIGHT / 2, 170, 0, Math.PI * 2);
  context2d.stroke();
  const angle = time / 400;
  context2d.fillStyle = "#38bdf8";
  context2d.beginPath();
  context2d.arc(
    WIDTH / 2 + Math.cos(angle) * 170,
    HEIGHT / 2 + Math.sin(angle) * 170,
    25,
    0,
    Math.PI * 2,
  );
  context2d.fill();
}

function drawCanvasWorkload(time: number, frame: number): void {
  if (!context2d) return;
  const ctx = context2d;
  ctx.fillStyle = "#173b20";
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  ctx.fillStyle = "#295f31";
  for (let x = 0; x < WIDTH; x += 64) {
    for (let y = 0; y < HEIGHT; y += 64) {
      if (((x + y) / 64) % 2 === 0) ctx.fillRect(x, y, 64, 64);
    }
  }

  ctx.strokeStyle = "#c3a877";
  ctx.lineWidth = 230;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(155, 545);
  ctx.bezierCurveTo(210, 85, 1040, 90, 1120, 360);
  ctx.bezierCurveTo(1180, 585, 620, 650, 155, 545);
  ctx.stroke();
  ctx.strokeStyle = "#49535d";
  ctx.lineWidth = 185;
  ctx.stroke();
  ctx.setLineDash([30, 25]);
  ctx.strokeStyle = "rgba(255,255,255,.45)";
  ctx.lineWidth = 5;
  ctx.stroke();
  ctx.setLineDash([]);

  for (let i = 0; i < 130; i += 1) {
    const life = (time * 0.08 + i * 19) % 280;
    const angle = i * 2.399 + time * 0.0002;
    const x = WIDTH / 2 + Math.cos(angle) * (170 + (i % 9) * 35);
    const y = HEIGHT / 2 + Math.sin(angle) * (80 + (i % 7) * 22);
    ctx.globalAlpha = 1 - life / 350;
    ctx.fillStyle = i % 3 === 0 ? "#facc15" : "#d7dde5";
    ctx.fillRect(x + life * 0.25, y + life * 0.08, 7 + (i % 4), 7 + (i % 4));
  }
  ctx.globalAlpha = 1;

  const colors = ["#ef4444", "#38bdf8", "#facc15", "#a855f7", "#22c55e", "#f97316"];
  for (let car = 0; car < colors.length; car += 1) {
    const angle = time * (0.00034 + car * 0.00001) + car * 0.78;
    const x = WIDTH / 2 + Math.cos(angle) * (395 - car * 7);
    const y = HEIGHT / 2 + Math.sin(angle) * (225 - car * 3);
    drawCar(ctx, x, y, angle + Math.PI / 2, colors[car] || "#ffffff");
  }

  ctx.fillStyle = "rgba(3,10,20,.82)";
  ctx.fillRect(28, 25, 350, 92);
  ctx.fillStyle = "#f8fafc";
  ctx.font = "bold 31px Arial";
  ctx.fillText("POCKET RALLY", 50, 65);
  ctx.fillStyle = "#7dd3fc";
  ctx.font = "bold 22px Arial";
  ctx.fillText("720p effects workload · frame " + frame, 50, 99);
}

let fuseBenchmarkLayer: HTMLCanvasElement | null = null;

function ensureFuseBenchmarkLayer(): HTMLCanvasElement {
  if (fuseBenchmarkLayer) return fuseBenchmarkLayer;
  const layer = document.createElement("canvas");
  layer.width = WIDTH;
  layer.height = HEIGHT;
  const ctx = layer.getContext("2d");
  if (!ctx) return layer;
  ctx.fillStyle = "#07141c";
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
  const boardGradient = ctx.createLinearGradient(28, 28, 780, 680);
  boardGradient.addColorStop(0, "#24593a");
  boardGradient.addColorStop(0.58, "#173d2a");
  boardGradient.addColorStop(1, "#081412");
  ctx.fillStyle = boardGradient;
  ctx.fillRect(28, 28, 780, 660);
  for (let y = 0; y < 11; y += 1) {
    for (let x = 0; x < 13; x += 1) {
      ctx.fillStyle = (x + y) % 2 === 0 ? "rgba(255,255,255,.035)" : "rgba(0,0,0,.035)";
      ctx.fillRect(28 + x * 60 + 1, 28 + y * 60 + 1, 58, 58);
      if (x === 0 || y === 0 || x === 12 || y === 10 || (x % 2 === 0 && y % 2 === 0)) {
        ctx.fillStyle = "#164e63";
        ctx.fillRect(28 + x * 60 + 7, 28 + y * 60 + 7, 46, 46);
        ctx.strokeStyle = "#67e8f9";
        ctx.lineWidth = 2;
        ctx.strokeRect(28 + x * 60 + 8, 28 + y * 60 + 8, 44, 44);
      }
    }
  }
  ctx.fillStyle = "#0f172a";
  ctx.fillRect(826, 28, 426, 660);
  ctx.strokeStyle = "#fbbf24";
  ctx.lineWidth = 4;
  ctx.strokeRect(28, 28, 780, 660);
  ctx.strokeRect(826, 28, 426, 660);
  ctx.fillStyle = "#fde68a";
  ctx.font = "900 36px Arial";
  ctx.fillText("FUSE RUNNERS", 850, 78);
  ctx.fillStyle = "#fff7ed";
  ctx.font = "900 22px Arial";
  ctx.fillText("Representative battle scene", 850, 112);
  fuseBenchmarkLayer = layer;
  return layer;
}

// This scene approximates FuseRunnersCoordinator's draw cost — it does not call the production
// renderer in tv.html. Breakable/blast/bomb/runner counts and the chain-reaction burst below are
// hand-tuned to the design doc's authored budgets (see docs/fuse-runners-game-design.md), so keep
// them in sync by hand if the real game's steady-state or peak effect load changes: notably the
// Power-Up/Breakable counts in §9.4, and the simultaneous chain-reaction detonations §8.2/§8.3 allow.
const FUSE_BURST_CYCLE_MS = 2_500;
const FUSE_BURST_DURATION_MS = 450;
const FUSE_BURST_COUNT = 10;

function drawFuseCompatibilityWorkload(time: number, frame: number): void {
  drawFuseRepresentativeWorkload(time, frame, false);
}

function drawFuseRichWorkload(time: number, frame: number): void {
  drawFuseRepresentativeWorkload(time, frame, true);
}

function drawFuseRepresentativeWorkload(time: number, frame: number, rich: boolean): void {
  if (!context2d) return;
  const ctx = context2d;
  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
  ctx.drawImage(ensureFuseBenchmarkLayer(), 0, 0);

  for (let breakable = 0; breakable < 42; breakable += 1) {
    const x = 28 + (1 + ((breakable * 5) % 11)) * 60;
    const y = 28 + (1 + ((breakable * 7) % 9)) * 60;
    ctx.fillStyle = breakable % 2 === 0 ? "#b45309" : "#92400e";
    ctx.fillRect(x + 10, y + 10, 40, 40);
    if (rich) {
      ctx.strokeStyle = "#fbbf24";
      ctx.lineWidth = 3;
      ctx.strokeRect(x + 12, y + 12, 36, 36);
    }
  }

  for (let blast = 0; blast < 18; blast += 1) {
    const x = 58 + ((blast * 3 + frame) % 12) * 60;
    const y = 58 + ((blast * 5) % 10) * 60;
    const pulse = 0.72 + Math.sin(time * 0.009 + blast) * 0.2;
    if (rich) {
      ctx.save();
      ctx.globalAlpha = pulse;
      ctx.shadowColor = "#fb923c";
      ctx.shadowBlur = 18;
      ctx.fillStyle = blast % 2 ? "#f97316" : "#fde047";
      drawBenchmarkSpark(ctx, x, y, 24 + (blast % 3) * 4, 10);
      ctx.restore();
    } else {
      ctx.globalAlpha = pulse;
      ctx.fillStyle = blast % 2 ? "#f97316" : "#fde047";
      ctx.fillRect(x - 19, y - 19, 38, 38);
      ctx.globalAlpha = 1;
    }
  }

  for (let bomb = 0; bomb < 8; bomb += 1) {
    const x = 58 + ((bomb * 4 + 2) % 12) * 60;
    const y = 58 + ((bomb * 7 + 1) % 10) * 60;
    ctx.fillStyle = "#05070b";
    ctx.beginPath();
    ctx.arc(x, y, 20, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = ["#f97316", "#38bdf8", "#a855f7", "#84cc16"][bomb % 4] ?? "#ffffff";
    ctx.lineWidth = 4;
    ctx.stroke();
    if (rich) {
      ctx.strokeStyle = "#fff7ed";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(x, y, 25, -Math.PI / 2, time * 0.004 + bomb);
      ctx.stroke();
    }
  }

  const runnerColors = ["#f97316", "#38bdf8", "#a855f7", "#84cc16"];
  for (let runner = 0; runner < 4; runner += 1) {
    const x = 100 + runner * 180 + Math.sin(time * 0.0018 + runner) * 30;
    const y = 150 + ((runner * 137) % 390) + Math.cos(time * 0.0015 + runner) * 22;
    ctx.save();
    if (rich) {
      ctx.shadowColor = runnerColors[runner] ?? "#ffffff";
      ctx.shadowBlur = 18;
    }
    ctx.fillStyle = runnerColors[runner] ?? "#ffffff";
    ctx.beginPath();
    ctx.arc(x, y, 25, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#fff7ed";
    ctx.beginPath();
    ctx.arc(x, y, 18, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#172033";
    ctx.beginPath();
    ctx.arc(x - 6, y - 4, 3, 0, Math.PI * 2);
    ctx.arc(x + 6, y - 4, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  if (rich) {
    for (let particle = 0; particle < 64; particle += 1) {
      const angle = particle * 2.399 + time * 0.0007;
      const radius = 45 + (particle % 9) * 22;
      ctx.globalAlpha = 0.2 + (particle % 4) * 0.15;
      ctx.fillStyle = particle % 3 === 0 ? "#fde047" : "#fb923c";
      ctx.save();
      ctx.translate(418 + Math.cos(angle) * radius, 358 + Math.sin(angle) * radius);
      ctx.rotate(angle);
      ctx.fillRect(-4, -4, 8, 8);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  const bursting = drawFuseChainReactionBurst(ctx, time, rich);

  ctx.fillStyle = "#fff7ed";
  ctx.font = "900 20px Arial";
  ctx.fillText(rich ? "RICH EFFECTS" : "COMPATIBILITY + SPRITES", 850, 158);
  ctx.fillText("4 runners · 8 bombs · 18 blasts", 850, 194);
  ctx.fillText(bursting ? "chain reaction burst" : "steady state", 850, 230);
  ctx.fillText("frame " + frame, 850, 266);
}

// Periodically detonates several bombs at once, the actual worst case the fuse-beat/hazard cues and
// FuseRunnersCoordinator's chain-reaction resolution allow — the steady-state loops above alone
// under-represent peak render cost, which is what p95/max render-work should be judged against.
function drawFuseChainReactionBurst(
  ctx: CanvasRenderingContext2D,
  time: number,
  rich: boolean,
): boolean {
  const cyclePosition = time % FUSE_BURST_CYCLE_MS;
  if (cyclePosition >= FUSE_BURST_DURATION_MS) return false;
  const progress = cyclePosition / FUSE_BURST_DURATION_MS;
  for (let burst = 0; burst < FUSE_BURST_COUNT; burst += 1) {
    const x = 58 + ((burst * 7 + 3) % 12) * 60;
    const y = 58 + ((burst * 11 + 2) % 10) * 60;
    const size = 20 + Math.sin(progress * Math.PI) * (rich ? 50 : 30);
    ctx.save();
    ctx.globalAlpha = Math.max(0, 1 - progress);
    if (rich) {
      ctx.shadowColor = "#fde047";
      ctx.shadowBlur = 26;
      ctx.fillStyle = burst % 2 === 0 ? "#f97316" : "#fde047";
      drawBenchmarkSpark(ctx, x, y, size, 10);
    } else {
      ctx.fillStyle = "#f97316";
      ctx.fillRect(x - size / 2, y - size / 2, size, size);
    }
    ctx.restore();
  }
  return true;
}

function drawBenchmarkSpark(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  points: number,
): void {
  ctx.beginPath();
  for (let point = 0; point < points * 2; point += 1) {
    const angle = (point / (points * 2)) * Math.PI * 2;
    const distance = point % 2 === 0 ? radius : radius * 0.45;
    const px = x + Math.cos(angle) * distance;
    const py = y + Math.sin(angle) * distance;
    if (point === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
}

function drawCar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rotation: number,
  color: string,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.fillStyle = "rgba(0,0,0,.4)";
  ctx.fillRect(-25, -40, 50, 86);
  ctx.fillStyle = "#111827";
  ctx.fillRect(-32, -29, 10, 22);
  ctx.fillRect(22, -29, 10, 22);
  ctx.fillRect(-32, 15, 10, 22);
  ctx.fillRect(22, 15, 10, 22);
  ctx.fillStyle = color;
  ctx.fillRect(-24, -42, 48, 84);
  ctx.fillStyle = "#bae6fd";
  ctx.fillRect(-17, -20, 34, 25);
  ctx.fillStyle = "rgba(255,255,255,.55)";
  ctx.fillRect(-18, -38, 36, 7);
  ctx.restore();
}

async function measureCpuSimulation(): Promise<CpuMetrics> {
  await nextFrame();
  const positions = new Float64Array(400);
  const velocities = new Float64Array(400);
  for (let index = 0; index < positions.length; index += 1) {
    positions[index] = index * 0.73;
    velocities[index] = (index % 11) * 0.013 + 0.01;
  }
  let updates = 0;
  let checksum = 0;
  const start = performance.now();
  while (performance.now() - start < 550) {
    for (let batch = 0; batch < 160; batch += 1) {
      for (let index = 0; index < positions.length; index += 1) {
        const velocity =
          ((velocities[index] || 0) + Math.sin(positions[index] || 0) * 0.0007) * 0.9998;
        let position = (positions[index] || 0) + velocity;
        if (position > 720) position -= 720;
        velocities[index] = velocity;
        positions[index] = position;
      }
      updates += positions.length;
    }
    checksum += positions[updates % positions.length] || 0;
    await nextFrame();
  }
  const durationMs = performance.now() - start;
  return {
    durationMs: round(durationMs),
    updates,
    updatesPerSecond: Math.round((updates * 1000) / durationMs),
    checksum: round(checksum),
  };
}

async function measureWebGl2(): Promise<WebGlDetails> {
  const webglCanvas = document.createElement("canvas");
  webglCanvas.width = WIDTH;
  webglCanvas.height = HEIGHT;
  let gl: WebGL2RenderingContext | null = null;
  try {
    gl = webglCanvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
    });
  } catch (error) {
    return unavailableWebGl(error instanceof Error ? error.message : String(error));
  }
  if (!gl) return unavailableWebGl("WebGL2 context creation returned null.");

  const debugInfo = gl.getExtension("WEBGL_debug_renderer_info") as {
    UNMASKED_VENDOR_WEBGL: number;
    UNMASKED_RENDERER_WEBGL: number;
  } | null;
  const details: WebGlDetails = {
    available: true,
    metrics: null,
    representativeMetrics: null,
    vendor: readGlString(gl, debugInfo ? debugInfo.UNMASKED_VENDOR_WEBGL : gl.VENDOR),
    renderer: readGlString(gl, debugInfo ? debugInfo.UNMASKED_RENDERER_WEBGL : gl.RENDERER),
    version: readGlString(gl, gl.VERSION),
    shadingLanguageVersion: readGlString(gl, gl.SHADING_LANGUAGE_VERSION),
    maxTextureSize: readGlNumber(gl, gl.MAX_TEXTURE_SIZE),
    maxRenderbufferSize: readGlNumber(gl, gl.MAX_RENDERBUFFER_SIZE),
    maxTextureUnits: readGlNumber(gl, gl.MAX_TEXTURE_IMAGE_UNITS),
    maxVertexTextureUnits: readGlNumber(gl, gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),
    maxSamples: readGlNumber(gl, gl.MAX_SAMPLES),
    contextRecovery: "not-run",
  };

  let program: WebGLProgram | null = null;
  let representativeProgram: WebGLProgram | null = null;
  let representativeTexture: WebGLTexture | null = null;
  let vertexArray: WebGLVertexArrayObject | null = null;
  try {
    program = createProgram(gl);
    representativeProgram = createRepresentativeProgram(gl);
    representativeTexture = createRepresentativeTexture(gl);
    vertexArray = gl.createVertexArray();
    gl.bindVertexArray(vertexArray);
    gl.useProgram(program);
    const timeLocation = gl.getUniformLocation(program, "u_time");
    const aspectLocation = gl.getUniformLocation(program, "u_aspect");
    gl.uniform1f(aspectLocation, WIDTH / HEIGHT);
    gl.viewport(0, 0, WIDTH, HEIGHT);

    await measureFrames(450, function (time) {
      drawWebGlFrame(gl, timeLocation, time);
    });
    details.metrics = await measureFrames(RENDER_DURATION_MS, function (time) {
      drawWebGlFrame(gl, timeLocation, time);
    });
    gl.useProgram(representativeProgram);
    const representativeTimeLocation = gl.getUniformLocation(representativeProgram, "u_time");
    const representativeAspectLocation = gl.getUniformLocation(representativeProgram, "u_aspect");
    gl.uniform1f(representativeAspectLocation, WIDTH / HEIGHT);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, representativeTexture);
    gl.uniform1i(gl.getUniformLocation(representativeProgram, "u_atlas"), 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    await measureFrames(450, function (time) {
      drawRepresentativeWebGlFrame(gl, representativeTimeLocation, time);
    });
    details.representativeMetrics = await measureFrames(RENDER_DURATION_MS, function (time) {
      drawRepresentativeWebGlFrame(gl, representativeTimeLocation, time);
    });
    const hardwareStressTiers = [
      { label: "dense 3D arena", instances: 1_024, passes: 4 },
      { label: "lighting and particles", instances: 4_096, passes: 6 },
      { label: "effects storm", instances: 16_384, passes: 8 },
      // This is the quick-run ceiling. Larger synchronous commands and pass
      // counts can monopolize software-rendered or older TV GPU queues instead
      // of producing a useful frame-time sample. The separate soak sustains
      // the heaviest passing tier to expose thermal or longevity degradation.
      { label: "heavy alpha overdraw", instances: 24_576, passes: 10 },
    ];
    const rendererIdentity = (details.vendor + " " + details.renderer).toLowerCase();
    const usesSoftwareRenderer =
      rendererIdentity.indexOf("swiftshader") >= 0 ||
      rendererIdentity.indexOf("llvmpipe") >= 0 ||
      rendererIdentity.indexOf("software") >= 0;
    const stressTiers = usesSoftwareRenderer
      ? hardwareStressTiers.slice(0, 1)
      : hardwareStressTiers;
    details.stressLevels = [];
    for (const tier of stressTiers) {
      setStage(
        6,
        "Stress testing WebGL2 · " + tier.label,
        "6 of 7 · " + (details.stressLevels.length + 1) + "/" + stressTiers.length,
      );
      await measureFrames(250, function (time) {
        drawRepresentativeWebGlStressFrame(
          gl,
          representativeTimeLocation,
          time,
          tier.instances,
          tier.passes,
        );
      });
      const metrics = await measureFrames(WEBGL_STRESS_DURATION_MS, function (time) {
        drawRepresentativeWebGlStressFrame(
          gl,
          representativeTimeLocation,
          time,
          tier.instances,
          tier.passes,
        );
      });
      details.stressLevels.push({
        ...tier,
        workUnits: tier.instances * tier.passes,
        metrics,
      });
      const measuredTier = details.stressLevels[details.stressLevels.length - 1];
      if (!measuredTier || !isWebGlStressLevelSafeForSoak(measuredTier)) break;
    }
    gl.disable(gl.BLEND);
    // Do not intentionally lose the quick-run context before a longevity test. The Reference TV
    // repeatedly failed to restore it, and the following fresh-context soak then crashed the
    // browser. Context recovery remains a separate physical promotion gate.
  } catch (error) {
    details.error = error instanceof Error ? error.message : String(error);
    details.metrics = null;
    details.representativeMetrics = null;
  } finally {
    if (!gl.isContextLost()) {
      if (representativeTexture) gl.deleteTexture(representativeTexture);
      if (vertexArray) gl.deleteVertexArray(vertexArray);
      if (representativeProgram) gl.deleteProgram(representativeProgram);
      if (program) gl.deleteProgram(program);
    }
  }
  return details;
}

function drawRepresentativeWebGlFrame(
  gl: WebGL2RenderingContext,
  timeLocation: WebGLUniformLocation | null,
  time: number,
): void {
  gl.clearColor(0.025, 0.075, 0.075, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.uniform1f(timeLocation, time * 0.001);
  // Board sprites, translucent blast/particle pass, and UI-like accents approximate the batching,
  // texture sampling, and alpha overdraw a real Fuse Runners WebGL renderer would need.
  gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, 320);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
  gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, 96);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, 32);
  gl.finish();
}

function drawRepresentativeWebGlStressFrame(
  gl: WebGL2RenderingContext,
  timeLocation: WebGLUniformLocation | null,
  time: number,
  instances: number,
  passes: number,
): void {
  gl.clearColor(0.025, 0.075, 0.075, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.uniform1f(timeLocation, time * 0.001);
  for (let pass = 0; pass < passes; pass += 1) {
    gl.blendFunc(gl.SRC_ALPHA, pass % 3 === 0 ? gl.ONE : gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, instances);
  }
  gl.finish();
}

interface WebGlStressRuntime {
  gl: WebGL2RenderingContext;
  timeLocation: WebGLUniformLocation | null;
  dispose(): void;
}

function createWebGlStressRuntime(): WebGlStressRuntime | null {
  const webglCanvas = document.createElement("canvas");
  webglCanvas.width = WIDTH;
  webglCanvas.height = HEIGHT;
  const gl = webglCanvas.getContext("webgl2", {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    preserveDrawingBuffer: false,
  });
  if (!gl) return null;
  let program: WebGLProgram | null = null;
  let texture: WebGLTexture | null = null;
  let vertexArray: WebGLVertexArrayObject | null = null;
  try {
    program = createRepresentativeProgram(gl);
    texture = createRepresentativeTexture(gl);
    vertexArray = gl.createVertexArray();
    gl.bindVertexArray(vertexArray);
    gl.useProgram(program);
    const timeLocation = gl.getUniformLocation(program, "u_time");
    gl.uniform1f(gl.getUniformLocation(program, "u_aspect"), WIDTH / HEIGHT);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1i(gl.getUniformLocation(program, "u_atlas"), 0);
    gl.viewport(0, 0, WIDTH, HEIGHT);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    return {
      gl,
      timeLocation,
      dispose: function (): void {
        if (gl.isContextLost()) return;
        if (texture) gl.deleteTexture(texture);
        if (vertexArray) gl.deleteVertexArray(vertexArray);
        if (program) gl.deleteProgram(program);
      },
    };
  } catch {
    if (texture) gl.deleteTexture(texture);
    if (vertexArray) gl.deleteVertexArray(vertexArray);
    if (program) gl.deleteProgram(program);
    return null;
  }
}

function drawWebGlFrame(
  gl: WebGL2RenderingContext,
  timeLocation: WebGLUniformLocation | null,
  time: number,
): void {
  gl.clearColor(0.025, 0.055, 0.1, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.uniform1f(timeLocation, time * 0.001);
  gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, 1_500);
  gl.finish();
}

function createProgram(gl: WebGL2RenderingContext): WebGLProgram {
  const vertexSource = `#version 300 es
precision highp float;
uniform float u_time;
uniform float u_aspect;
out vec3 v_color;
void main() {
  vec2 corners[6] = vec2[6](vec2(-1.,-1.),vec2(1.,-1.),vec2(-1.,1.),vec2(-1.,1.),vec2(1.,-1.),vec2(1.,1.));
  float id = float(gl_InstanceID);
  float angle = id * 2.399963 + u_time * (0.15 + mod(id, 7.0) * 0.02);
  float radius = 0.05 + mod(id * 0.017, 0.9);
  vec2 center = vec2(cos(angle) * radius / u_aspect, sin(angle) * radius);
  float size = 0.0025 + mod(id, 9.0) * 0.00035;
  gl_Position = vec4(center + corners[gl_VertexID] * size, 0., 1.);
  v_color = 0.45 + 0.55 * cos(vec3(0., 2., 4.) + id * 0.07);
}`;
  const fragmentSource = `#version 300 es
precision mediump float;
in vec3 v_color;
out vec4 outColor;
void main() { outColor = vec4(v_color, 0.85); }`;
  const vertex = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = gl.createProgram();
  if (!program) throw new Error("Could not allocate a WebGL2 program.");
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program) || "Unknown link error";
    gl.deleteProgram(program);
    throw new Error("WebGL2 program link failed: " + log);
  }
  return program;
}

function createRepresentativeProgram(gl: WebGL2RenderingContext): WebGLProgram {
  const vertexSource = `#version 300 es
precision highp float;
uniform float u_time;
uniform float u_aspect;
out vec2 v_uv;
out vec4 v_tint;
void main() {
  vec2 corners[6] = vec2[6](vec2(-1.,-1.),vec2(1.,-1.),vec2(-1.,1.),vec2(-1.,1.),vec2(1.,-1.),vec2(1.,1.));
  vec2 uvCorners[6] = vec2[6](vec2(0.,0.),vec2(1.,0.),vec2(0.,1.),vec2(0.,1.),vec2(1.,0.),vec2(1.,1.));
  float id = float(gl_InstanceID);
  float column = mod(id, 20.0);
  float row = floor(mod(id, 240.0) / 20.0);
  vec2 center = vec2(-0.88 + column * 0.074, -0.78 + row * 0.14);
  float kind = mod(id, 4.0);
  float animated = step(239.5, id);
  float angle = id * 2.399963 + u_time * (0.7 + mod(id, 5.0) * 0.08);
  center = mix(center, vec2(cos(angle) * 0.68 / u_aspect, sin(angle) * 0.72), animated);
  float pulse = 1.0 + animated * (0.18 * sin(u_time * 7.0 + id));
  vec2 size = vec2(0.032 / u_aspect, 0.052) * pulse;
  gl_Position = vec4(center + corners[gl_VertexID] * size, 0., 1.);
  vec2 tile = vec2(mod(kind, 2.0), floor(kind / 2.0));
  v_uv = (uvCorners[gl_VertexID] + tile) * 0.5;
  vec3 palette = 0.58 + 0.42 * cos(vec3(0.0, 2.1, 4.2) + id * 0.37);
  v_tint = vec4(palette, mix(0.92, 0.48, animated));
}`;
  const fragmentSource = `#version 300 es
precision mediump float;
uniform sampler2D u_atlas;
in vec2 v_uv;
in vec4 v_tint;
out vec4 outColor;
void main() {
  vec4 sprite = texture(u_atlas, v_uv);
  if (sprite.a < 0.03) discard;
  outColor = vec4(sprite.rgb * v_tint.rgb, sprite.a * v_tint.a);
}`;
  const vertex = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = gl.createProgram();
  if (!program) throw new Error("Could not allocate the representative WebGL2 program.");
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program) || "Unknown link error";
    gl.deleteProgram(program);
    throw new Error("Representative WebGL2 program link failed: " + log);
  }
  return program;
}

function createRepresentativeTexture(gl: WebGL2RenderingContext): WebGLTexture {
  const size = 64;
  const pixels = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const localX = (x % 32) - 15.5;
      const localY = (y % 32) - 15.5;
      const tileX = x < 32 ? 0 : 1;
      const tileY = y < 32 ? 0 : 1;
      const distance = Math.sqrt(localX * localX + localY * localY);
      const diamond = Math.abs(localX) + Math.abs(localY);
      const alpha =
        tileX === 0 && tileY === 0
          ? distance < 14
            ? 255
            : 0
          : tileX === 1 && tileY === 0
            ? diamond < 19
              ? 230
              : 0
            : tileX === 0
              ? Math.max(0, Math.round(255 - distance * 16))
              : Math.abs(localX) < 5 || Math.abs(localY) < 5
                ? 220
                : 0;
      const offset = (y * size + x) * 4;
      pixels[offset] = tileY === 0 ? 255 : 251;
      pixels[offset + 1] = tileX === 0 ? 244 : 146;
      pixels[offset + 2] = tileY === 0 ? 214 : 60;
      pixels[offset + 3] = alpha;
    }
  }
  const texture = gl.createTexture();
  if (!texture) throw new Error("Could not allocate the representative WebGL2 texture.");
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  return texture;
}

function compileShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("Could not allocate a WebGL2 shader.");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) || "Unknown compilation error";
    gl.deleteShader(shader);
    throw new Error("WebGL2 shader compilation failed: " + log);
  }
  return shader;
}

function unavailableWebGl(error: string): WebGlDetails {
  return {
    available: false,
    metrics: null,
    representativeMetrics: null,
    vendor: "",
    renderer: "",
    version: "",
    shadingLanguageVersion: "",
    maxTextureSize: 0,
    maxRenderbufferSize: 0,
    maxTextureUnits: 0,
    maxVertexTextureUnits: 0,
    maxSamples: 0,
    contextRecovery: "not-run",
    error,
  };
}

function showResult(result: BenchmarkResult): void {
  latestResult = result;
  const displayedSoak = result.webgl2.soak ?? result.soak;
  if (displayedSoak) {
    progress.max = SOAK_DURATION_MS;
    progress.value = displayedSoak.completedDurationMs;
  } else {
    progress.max = 7;
    progress.value = 7;
  }
  benchmarkStatus.textContent = "Benchmark complete";
  benchmarkStatus.setAttribute("data-benchmark-complete", "true");
  benchmarkStep.textContent = result.classification.title;
  summary.className = "summary " + (result.classification.meetsMinimum ? "pass" : "fail");
  summary.innerHTML =
    "<h2>" +
    escapeHtml(result.classification.title) +
    "</h2><p>" +
    escapeHtml(result.classification.summary) +
    "</p><p><strong>Fuse Runners:</strong> " +
    (result.classification.fuseRunnersCompatibilityPass === false
      ? "the representative battle missed the 30 fps floor even in compatibility mode."
      : result.classification.recommendedFusePreset === "canvas-rich"
        ? "rich effects passed this representative run."
        : "use compatibility graphics with lightweight sprites.") +
    "</p>" +
    (result.soak
      ? "<p><strong>Canvas 30 fps soak:</strong> " +
        escapeHtml(
          result.soak.aggregate.frames.fps +
            " fps aggregate · " +
            result.soak.aggregate.frames.framesOver100Ms +
            " stalls ≥100 ms · " +
            result.soak.windows.length +
            " recorded windows.",
        ) +
        "</p>"
      : "<p><strong>Canvas soak:</strong> not run.</p>") +
    (result.webgl2.soak
      ? "<p><strong>WebGL2 stress soak:</strong> " +
        escapeHtml(
          (result.webgl2.soak.stressLabel || "representative stress") +
            " · " +
            result.webgl2.soak.aggregate.frames.fps +
            " fps aggregate · " +
            result.webgl2.soak.aggregate.frames.framesOver100Ms +
            " stalls ≥100 ms.",
        ) +
        "</p>"
      : "<p><strong>WebGL2 soak:</strong> not run.</p>");
  jsonOutput.value = JSON.stringify(result, null, 2);
  report.classList.remove("hidden");
  sendButton.disabled = false;
  sendButton.textContent = "Send Benchmark Data to Collector";
  sendStatus.textContent = "";
  try {
    window.localStorage.setItem("tvGraphicsBenchmark:lastResult", jsonOutput.value);
  } catch {
    // Private browsing and constrained TV storage may reject localStorage.
  }
}

function readEnvironment(): BenchmarkResult["environment"] {
  const navigatorDetails = navigator as Navigator & {
    deviceMemory?: number;
    hardwareConcurrency?: number;
  };
  const browser = detectBrowser(navigator.userAgent);
  return {
    userAgent: navigator.userAgent,
    browserName: browser.name,
    browserVersion: browser.version,
    platform: navigator.platform || "",
    language: navigator.language || "",
    reportedTvModel: normalizedTvModel(),
    detectedTvModel: detectTvModel(navigator.userAgent),
    viewport: window.innerWidth + "×" + window.innerHeight,
    screen: window.screen.width + "×" + window.screen.height,
    devicePixelRatio: window.devicePixelRatio || 1,
    hardwareConcurrency: navigatorDetails.hardwareConcurrency || null,
    deviceMemoryGb: navigatorDetails.deviceMemory || null,
  };
}

function readFeatures(webgl2Available: boolean): BenchmarkResult["features"] {
  const audioWindow = window as Window & {
    AudioContext?: unknown;
    webkitAudioContext?: unknown;
  };
  return {
    canvas2d: !!document.createElement("canvas").getContext("2d"),
    webgl1: supportsWebGl1(),
    webgl2: webgl2Available,
    webAudio: !!(audioWindow.AudioContext || audioWindow.webkitAudioContext),
    gamepadApi: typeof navigator.getGamepads === "function",
    localStorage: supportsLocalStorage(),
    webWorkers: typeof Worker !== "undefined",
  };
}

function createWebGlRunId(): string {
  const randomPart = Math.floor(Math.random() * 0x100000000)
    .toString(36)
    .padStart(7, "0");
  return "webgl-" + Date.now().toString(36) + "-" + randomPart;
}

function createWebGlProgressReport(options: {
  runId: string;
  sequence: number;
  status: TvBenchmarkProgressReport["status"];
  selected: ReturnType<typeof webGlLongevityProfile>;
  checkpoint?: SoakCheckpoint | undefined;
  contextLost: boolean;
  error?: string | undefined;
}): TvBenchmarkProgressReport {
  const source = latestResult;
  if (!source) throw new Error("A quick benchmark result is required for WebGL diagnostics.");
  source.environment.reportedTvModel = normalizedTvModel();
  return {
    kind: "webgl2-soak-progress",
    schemaVersion: 1,
    runId: options.runId,
    benchmarkVersion: BENCHMARK_VERSION,
    capturedAt: new Date().toISOString(),
    status: options.status,
    sequence: options.sequence,
    elapsedMs: options.checkpoint?.elapsedMs ?? 0,
    targetDurationMs: 600_000,
    checkpointIntervalMs: WEBGL_CHECKPOINT_INTERVAL_MS,
    pageVisibility: document.hidden ? "hidden" : "visible",
    contextLost: options.contextLost,
    profile: {
      ...options.selected,
      width: WIDTH,
      height: HEIGHT,
    },
    environment: { ...source.environment },
    webgl2: {
      vendor: source.webgl2.vendor,
      renderer: source.webgl2.renderer,
      version: source.webgl2.version,
      shadingLanguageVersion: source.webgl2.shadingLanguageVersion,
      maxTextureSize: source.webgl2.maxTextureSize,
      maxRenderbufferSize: source.webgl2.maxRenderbufferSize,
      maxTextureUnits: source.webgl2.maxTextureUnits,
      maxVertexTextureUnits: source.webgl2.maxVertexTextureUnits,
      maxSamples: source.webgl2.maxSamples,
    },
    ...(options.checkpoint
      ? {
          aggregate: options.checkpoint.aggregate,
          latestWindow: options.checkpoint.latestWindow,
          lastFrameGapMs: options.checkpoint.lastFrameGapMs,
        }
      : {}),
    ...(options.error ? { error: options.error.slice(0, 500) } : {}),
  };
}

function persistWebGlProgress(payload: TvBenchmarkProgressReport): void {
  try {
    window.localStorage.setItem(WEBGL_PROGRESS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // The collector still receives checkpoints when constrained TV storage is unavailable.
  }
}

async function postProgressCheckpoint(payload: TvBenchmarkProgressReport): Promise<void> {
  try {
    await sendBenchmarkSubmission(payload, true);
  } catch {
    // The same payload remains in localStorage and is offered for recovery after a reload.
  }
}

function restoreInterruptedWebGlProgress(): void {
  let restored: unknown;
  try {
    const raw = window.localStorage.getItem(WEBGL_PROGRESS_STORAGE_KEY);
    if (!raw) return;
    restored = JSON.parse(raw);
  } catch {
    return;
  }
  const parsed = parseTvBenchmarkSubmission(restored);
  if (!parsed || !("kind" in parsed) || parsed.kind !== "webgl2-soak-progress") return;
  if (parsed.status === "completed") return;
  recoveredWebGlProgress = parsed;
  recoveryDetail.textContent =
    "Run " +
    parsed.runId +
    " last recorded " +
    Math.round(parsed.elapsedMs / 1_000) +
    " seconds, checkpoint " +
    parsed.sequence +
    ", status " +
    parsed.status +
    (parsed.contextLost ? ", after WebGL context loss." : ".");
  recovery.classList.remove("hidden");
}

async function sendRecoveredWebGlProgress(payload: TvBenchmarkProgressReport): Promise<void> {
  sendRecoveredButton.disabled = true;
  recoveredSendStatus.textContent = "Sending recovered checkpoint…";
  try {
    await sendBenchmarkSubmission(payload, true);
    recoveredSendStatus.textContent = "Recovered checkpoint accepted.";
  } catch (error) {
    sendRecoveredButton.disabled = false;
    recoveredSendStatus.textContent =
      "Could not submit: " + (error instanceof Error ? error.message : String(error));
  }
}

async function sendBenchmarkSubmission(
  submission: BenchmarkResult | TvBenchmarkProgressReport,
  keepalive = false,
): Promise<void> {
  const response = await fetch(collectorUrl(), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(submission),
    credentials: "omit",
    keepalive,
  });
  if (!response.ok) throw new Error("Collector returned " + response.status + ".");
}

async function sendResults(): Promise<void> {
  if (!latestResult || sendButton.disabled) return;
  latestResult.environment.reportedTvModel = normalizedTvModel();
  jsonOutput.value = JSON.stringify(latestResult, null, 2);
  sendButton.disabled = true;
  sendButton.textContent = "Sending…";
  sendStatus.textContent = "Submitting only the displayed benchmark report.";
  try {
    await sendBenchmarkSubmission(latestResult);
    sendButton.textContent = "Benchmark Data Sent";
    sendStatus.textContent = "Thank you—this result was accepted by the collector.";
  } catch (error) {
    sendButton.disabled = false;
    sendButton.textContent = "Try Sending Again";
    sendStatus.textContent =
      "Could not submit: " + (error instanceof Error ? error.message : String(error));
  }
}

function collectorUrl(): string {
  const configured = import.meta.env.VITE_MULTIPLAYER_WS_URL as string | undefined;
  if (configured) {
    const url = new URL(configured);
    url.protocol = url.protocol === "wss:" ? "https:" : "http:";
    url.pathname = "/multiplayer/tv-benchmarks";
    url.search = "";
    url.hash = "";
    return url.toString();
  }
  const protocol = window.location.protocol === "https:" ? "https:" : "http:";
  return protocol + "//" + window.location.hostname + ":8787/multiplayer/tv-benchmarks";
}

function detectBrowser(userAgent: string): { name: string; version: string } {
  const chrome = userAgent.match(/(?:Chrome|Chromium)\/([0-9.]+)/i);
  if (chrome) return { name: "Chromium", version: chrome[1] || "Unknown" };
  const webOs = userAgent.match(/Web0S[\/. ]([0-9.]+)/i);
  if (webOs) return { name: "webOS Browser", version: webOs[1] || "Unknown" };
  return { name: "Unknown", version: "Unknown" };
}

function detectTvModel(userAgent: string): string | null {
  const explicitModel = userAgent.match(/(?:Model|Device)[\/:= ]+([A-Z0-9][A-Z0-9._-]{2,})/i);
  if (explicitModel) return (explicitModel[1] || "").slice(0, 120) || null;
  const lgModel = userAgent.match(/\b(LG[A-Z0-9-]{4,})\b/i);
  return lgModel ? (lgModel[1] || "").slice(0, 120) || null : null;
}

function normalizedTvModel(): string | null {
  const value = tvModelInput.value.replace(/^\s+|\s+$/g, "").slice(0, 120);
  return value || null;
}

function supportsWebGl1(): boolean {
  try {
    const testCanvas = document.createElement("canvas");
    const gl = testCanvas.getContext("webgl");
    if (!gl) return false;
    const loseContext = gl.getExtension("WEBGL_lose_context");
    if (loseContext) loseContext.loseContext();
    return true;
  } catch {
    return false;
  }
}

function supportsLocalStorage(): boolean {
  try {
    const key = "tvGraphicsBenchmark:storageTest";
    window.localStorage.setItem(key, "1");
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function copyResults(): void {
  jsonOutput.focus();
  jsonOutput.select();
  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  }
  requiredElement<HTMLElement>("copy-status").textContent = copied
    ? "Copied to clipboard."
    : "Select the result text and copy it manually.";
}

function setStage(value: number, text: string, step: string): void {
  progress.value = value;
  benchmarkStatus.textContent = text;
  benchmarkStatus.removeAttribute("data-benchmark-complete");
  benchmarkStep.textContent = step;
}

function setMetric(name: string, value: string, detail: string): void {
  requiredElement<HTMLElement>(name + "-result").textContent = value;
  requiredElement<HTMLElement>(name + "-detail").textContent = detail;
}

function formatFrameDetails(metrics: FrameMetrics): string {
  return (
    "median " +
    metrics.medianFrameMs +
    " ms · p95 " +
    metrics.p95FrameMs +
    " ms · max " +
    metrics.maxFrameMs +
    " ms · " +
    metrics.longFrames +
    " long frames · " +
    metrics.framesOver100Ms +
    " stalls ≥100 ms"
  );
}

function formatSceneDetails(metrics: SceneMetrics): string {
  return (
    formatFrameDetails(metrics.frames) +
    " · draw p95 " +
    metrics.renderWork.p95Ms +
    " ms · max " +
    metrics.renderWork.maxMs +
    " ms · " +
    metrics.renderWork.overBudgetCalls +
    " draws over 28 ms"
  );
}

function formatCompactNumber(value: number): string {
  if (value >= 1_000_000) return round(value / 1_000_000) + "M";
  if (value >= 1_000) return round(value / 1_000) + "K";
  return String(value);
}

function readGlString(gl: WebGL2RenderingContext, name: number): string {
  const value = gl.getParameter(name) as unknown;
  return typeof value === "string" ? value : "Unknown";
}

function readGlNumber(gl: WebGL2RenderingContext, name: number): number {
  const value = gl.getParameter(name) as unknown;
  return typeof value === "number" ? value : 0;
}

function nextFrame(): Promise<void> {
  return new Promise(function (resolve) {
    window.requestAnimationFrame(function () {
      resolve();
    });
  });
}

function drawIdleScene(): void {
  if (!context2d) return;
  drawCanvasWorkload(0, 0);
  context2d.fillStyle = "rgba(2,6,23,.72)";
  context2d.fillRect(0, 0, WIDTH, HEIGHT);
  context2d.fillStyle = "#f8fafc";
  context2d.font = "bold 48px Arial";
  context2d.textAlign = "center";
  context2d.fillText("READY TO BENCHMARK", WIDTH / 2, HEIGHT / 2);
  context2d.textAlign = "start";
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function requiredElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error("Missing benchmark element #" + id);
  return element as T;
}
